import React, { useEffect, useState, useCallback } from 'react';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import NoRecordFound from '../../../components/common/no-record-found';

const ROWS_PER_PAGE = 34;

const SalesReport = () => {
  const [data, setData] = useState([]);
  const [expandedContracts, setExpandedContracts] = useState({});
  const [expandedSalesOrders, setExpandedSalesOrders] = useState({});
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    const delay = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(delay);
  }, [search]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ page, limit: ROWS_PER_PAGE, search: debouncedSearch.trim() });
      const res = await fetch(`${API_BASE_URL}/api/report/sales-report?${params.toString()}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch');
      const result = await res.json();
      setData(result.data || []);
      setTotal(result.total || result.data?.length || 0);
    } catch (err) {
      setError('Failed to load sales report');
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch]);

  useEffect(() => {
    fetchData();
  }, [debouncedSearch, page, fetchData]);

  const totalPages = Math.ceil(total / ROWS_PER_PAGE);
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };
  const handlePrevPage = () => setPage((p) => Math.max(1, p - 1));
  const handleNextPage = () => setPage((p) => Math.min(totalPages, p + 1));

  const toggleContract = (contractId) => {
    setExpandedContracts((prev) => ({ ...prev, [contractId]: !prev[contractId] }));
  };
  const toggleSalesOrder = (saleId) => {
    setExpandedSalesOrders((prev) => ({ ...prev, [saleId]: !prev[saleId] }));
  };

  return (
    <div className="max-w-8xl mx-auto p-3 bg-white rounded-md shadow-sm border border-gray-300">
      <div className="flex flex-col sm:flex-row justify-between items-center pb-2 mb-3 border-b border-gray-200 gap-2">
        <div>
          <h2 className="text-[15px] font-semibold text-gray-900 tracking-tight">Sales Contract Register</h2>
          <p className="text-[11px] text-gray-500 mt-0.5">Contract-wise sales and delivery order summary</p>
        </div>
        <div className="flex gap-1 w-full sm:w-auto">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
              <svg className="h-3.5 w-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search by contract, buyer, commodity..."
              value={search}
              onChange={handleSearchChange}
              className="pl-8 pr-2 py-1.5 border border-gray-300 rounded-sm focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-colors text-[12px] w-full sm:w-64 outline-none text-gray-900 placeholder-gray-500"
            />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-12 space-y-3">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-600"></div>
          <p className="text-[13px] text-gray-600">Loading sales data...</p>
        </div>
      ) : error ? (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded-lg">
          <h3 className="text-[13px] font-medium text-red-800">Error loading data</h3>
          <p className="text-[13px] text-red-700 mt-1">{error}</p>
        </div>
      ) : data.length === 0 ? (
        <NoRecordFound message="No sales contracts found" subMessage="Try adjusting your search" iconClass="text-gray-400" />
      ) : (
        <>
          <div className="overflow-x-auto border border-gray-300 rounded-sm">
            <table className="min-w-full text-left border-collapse">
              <thead className="bg-[#f4f4f4] border-b border-gray-300">
                <tr>
                  <th className="px-3 py-2 font-semibold text-gray-800 uppercase tracking-wider text-[11px] border-r border-gray-200 last:border-r-0 whitespace-nowrap">#</th>
                  <th className="px-3 py-2 font-semibold text-gray-800 uppercase tracking-wider text-[11px] border-r border-gray-200 last:border-r-0 whitespace-nowrap">Contract No.</th>
                  <th className="px-3 py-2 font-semibold text-gray-800 uppercase tracking-wider text-[11px] border-r border-gray-200 last:border-r-0 whitespace-nowrap">Contract Date</th>
                  <th className="px-3 py-2 font-semibold text-gray-800 uppercase tracking-wider text-[11px] border-r border-gray-200 last:border-r-0 whitespace-nowrap">Buyer</th>
                  <th className="px-3 py-2 font-semibold text-gray-800 uppercase tracking-wider text-[11px] border-r border-gray-200 last:border-r-0 whitespace-nowrap">Commodity</th>
                  <th className="px-3 py-2 font-semibold text-gray-800 uppercase tracking-wider text-[11px] border-r border-gray-200 last:border-r-0 whitespace-nowrap text-right">Contract Qty</th>
                  <th className="px-3 py-2 font-semibold text-gray-800 uppercase tracking-wider text-[11px] border-r border-gray-200 last:border-r-0 whitespace-nowrap text-right">Sale Price</th>
                  <th className="px-3 py-2 font-semibold text-gray-800 uppercase tracking-wider text-[11px] border-r border-gray-200 last:border-r-0 whitespace-nowrap">Remarks</th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {data.map((contract, idx) => (
                  <React.Fragment key={contract.contract_no}>
                    <tr
                      className="hover:bg-blue-50/50 transition-colors duration-150 border-b border-gray-200 last:border-b-0 cursor-pointer bg-blue-50/30"
                      onClick={() => toggleContract(contract.sales_contract_id)}
                    >
                      <td className="px-3 py-1.5 text-gray-800 text-[12px] border-r border-gray-100 last:border-r-0 whitespace-nowrap font-bold text-center">{expandedContracts[contract.sales_contract_id] ? '−' : '+'}</td>
                      <td className="px-3 py-1.5 text-[#15803d] text-[12px] border-r border-gray-100 last:border-r-0 whitespace-nowrap font-bold">{contract.contract_no}</td>
                      <td className="px-3 py-1.5 text-gray-800 text-[12px] border-r border-gray-100 last:border-r-0 whitespace-nowrap">{contract.contract_date}</td>
                      <td className="px-3 py-1.5 text-gray-800 text-[12px] border-r border-gray-100 last:border-r-0 whitespace-nowrap">{contract.buyer}</td>
                      <td className="px-3 py-1.5 text-gray-800 text-[12px] border-r border-gray-100 last:border-r-0 whitespace-nowrap">{contract.commodity}</td>
                      <td className="px-3 py-1.5 text-gray-800 text-[12px] border-r border-gray-100 last:border-r-0 whitespace-nowrap text-right font-semibold">{contract.contract_qty}</td>
                      <td className="px-3 py-1.5 text-gray-800 text-[12px] border-r border-gray-100 last:border-r-0 whitespace-nowrap text-right font-semibold">{contract.sale_price}</td>
                      <td className="px-3 py-1.5 text-gray-800 text-[12px] border-r border-gray-100 last:border-r-0 whitespace-nowrap">{contract.remarks}</td>
                    </tr>
                    {expandedContracts[contract.sales_contract_id] && (
                      <tr>
                        <td colSpan={8} className="bg-gray-50 px-3 py-1.5">
                          <div className="font-bold text-[#15803d] mb-1">Sales Orders</div>
                          <div className="overflow-x-auto border border-gray-200 rounded-sm mb-2 mt-1">
                            <table className="min-w-full text-left border-collapse bg-white">
                              <thead className="bg-green-50/50 border-b border-gray-200">
                                <tr>
                                  <th className="px-3 py-1.5 font-semibold text-green-800 uppercase tracking-wider text-[10px] border-r border-gray-200 last:border-r-0 whitespace-nowrap">#</th>
                                  <th className="px-3 py-1.5 font-semibold text-green-800 uppercase tracking-wider text-[10px] border-r border-gray-200 last:border-r-0 whitespace-nowrap">Order Date</th>
                                  <th className="px-3 py-1.5 font-semibold text-green-800 uppercase tracking-wider text-[10px] border-r border-gray-200 last:border-r-0 whitespace-nowrap text-right">Qty (MT)</th>
                                  <th className="px-3 py-1.5 font-semibold text-green-800 uppercase tracking-wider text-[10px] border-r border-gray-200 last:border-r-0 whitespace-nowrap text-right">Sale Price/MT</th>
                                  <th className="px-3 py-1.5 font-semibold text-green-800 uppercase tracking-wider text-[10px] border-r border-gray-200 last:border-r-0 whitespace-nowrap">Remarks</th>
                                </tr>
                              </thead>
                              <tbody>
                                {contract.sales_orders && contract.sales_orders.length > 0 ? (
                                  contract.sales_orders.map((so, soIdx) => (
                                    <React.Fragment key={so.sale_id}>
                                      <tr
                                        className="hover:bg-green-50/50 transition-colors duration-150 border-b border-gray-100 last:border-b-0 cursor-pointer bg-green-50/20"
                                        onClick={() => toggleSalesOrder(so.sale_id)}
                                      >
                                        <td className="px-3 py-1 text-green-900 text-[11px] border-r border-gray-100 last:border-r-0 whitespace-nowrap font-bold text-center">{expandedSalesOrders[so.sale_id] ? '−' : '+'}</td>
                                        <td className="px-3 py-1 text-green-900 text-[11px] border-r border-gray-100 last:border-r-0 whitespace-nowrap">{so.order_date}</td>
                                        <td className="px-3 py-1 text-green-900 text-[11px] border-r border-gray-100 last:border-r-0 whitespace-nowrap text-right font-semibold">{so.quantity_mt}</td>
                                        <td className="px-3 py-1 text-green-900 text-[11px] border-r border-gray-100 last:border-r-0 whitespace-nowrap text-right font-semibold">{so.sale_price_per_mt}</td>
                                        <td className="px-3 py-1 text-green-900 text-[11px] border-r border-gray-100 last:border-r-0 whitespace-nowrap">{so.remarks}</td>
                                      </tr>
                                    {expandedSalesOrders[so.sale_id] && (
                                      <tr>
                                        <td colSpan={5} className="bg-gray-100 px-2 py-1.5">
                                          <div className="font-bold text-green-700 mb-1">Delivery Orders</div>
                                          <div className="overflow-x-auto border border-gray-200 rounded-sm mb-2 mt-1">
                                            <table className="min-w-full text-left border-collapse bg-white">
                                              <thead className="bg-yellow-50/50 border-b border-gray-200">
                                                <tr>
                                                  <th className="px-3 py-1 font-semibold text-yellow-800 uppercase tracking-wider text-[10px] border-r border-gray-200 last:border-r-0 whitespace-nowrap">DO No</th>
                                                  <th className="px-3 py-1 font-semibold text-yellow-800 uppercase tracking-wider text-[10px] border-r border-gray-200 last:border-r-0 whitespace-nowrap">DO Date</th>
                                                  <th className="px-3 py-1 font-semibold text-yellow-800 uppercase tracking-wider text-[10px] border-r border-gray-200 last:border-r-0 whitespace-nowrap text-right">Qty (MT)</th>
                                                  <th className="px-3 py-1 font-semibold text-yellow-800 uppercase tracking-wider text-[10px] border-r border-gray-200 last:border-r-0 whitespace-nowrap">Warehouse</th>
                                                  <th className="px-3 py-1 font-semibold text-yellow-800 uppercase tracking-wider text-[10px] border-r border-gray-200 last:border-r-0 whitespace-nowrap">Buyer</th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {so.delivery_orders && so.delivery_orders.length > 0 ? (
                                                  so.delivery_orders.map((doo, doIdx) => (
                                                    <tr key={doo.do_id} className="hover:bg-yellow-50/50 transition-colors duration-150 border-b border-gray-100 last:border-b-0 bg-yellow-50/20">
                                                      <td className="px-3 py-1 text-yellow-900 text-[11px] border-r border-gray-100 last:border-r-0 whitespace-nowrap font-medium">{doo.do_number}</td>
                                                      <td className="px-3 py-1 text-yellow-900 text-[11px] border-r border-gray-100 last:border-r-0 whitespace-nowrap">{doo.do_date}</td>
                                                      <td className="px-3 py-1 text-yellow-900 text-[11px] border-r border-gray-100 last:border-r-0 whitespace-nowrap text-right font-semibold">{doo.quantity_mt}</td>
                                                      <td className="px-3 py-1 text-yellow-900 text-[11px] border-r border-gray-100 last:border-r-0 whitespace-nowrap">{doo.warehouse_location}</td>
                                                      <td className="px-3 py-1 text-yellow-900 text-[11px] border-r border-gray-100 last:border-r-0 whitespace-nowrap">{doo.buyer_name}</td>
                                                    </tr>
                                                ))
                                              ) : (
                                                <tr>
                                                  <td colSpan={5} className="text-center text-[12px] text-gray-500 bg-yellow-50 py-1.5">No delivery orders found.</td>
                                                </tr>
                                              )}
                                            </tbody>
                                          </table>
                                          </div>
                                        </td>
                                      </tr>
                                    )}
                                  </React.Fragment>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan={5} className="text-center text-[12px] text-gray-500 bg-green-50 py-1.5">No sales orders found.</td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex flex-col sm:flex-row justify-between items-center mt-3 text-[12px]">
              <div className="text-gray-600 mb-2 sm:mb-0">
                Showing <span className="font-semibold text-gray-900">{((page - 1) * ROWS_PER_PAGE) + 1}</span> to <span className="font-semibold text-gray-900">{Math.min(page * ROWS_PER_PAGE, total)}</span> of <span className="font-semibold text-gray-900">{total}</span> records
              </div>
              <div className="inline-flex shadow-sm rounded-sm">
                <button
                  onClick={handlePrevPage}
                  disabled={page === 1}
                  className="px-3 py-1 border border-gray-300 bg-white text-gray-800 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed font-medium rounded-l-sm transition-colors"
                >
                  Previous
                </button>
                <div className="px-3 py-1 border-t border-b border-gray-300 bg-gray-50 text-gray-900 font-medium">
                  {page} / {totalPages}
                </div>
                <button
                  onClick={handleNextPage}
                  disabled={page === totalPages}
                  className="px-3 py-1 border border-gray-300 bg-white text-gray-800 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed font-medium rounded-r-sm transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SalesReport;
